import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faTimes, faClipboardList, faBuilding, faTasks,
  faUserTag, faClipboardCheck, faComment, faStar, faCalendarAlt,
  faExclamationTriangle, faCheckCircle, faEraser
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// ============================================================
// 1. CACHE DEXIE (24h) – réutilisation du même store "missions"
// ============================================================
const db = new Dexie('MissionsCacheDB');
db.version(1).stores({
  missions: 'key, data, timestamp',
  entreprises: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key) {
  try {
    const entry = await db.missions.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.missions.put({ key, data, timestamp: Date.now() });
}
async function clearRapportCache() {
  // Supprime toutes les entrées dont la clé commence par "rapport_"
  const all = await db.missions.toArray();
  const keys = all.filter(e => e.key.startsWith('rapport_')).map(e => e.key);
  await db.missions.bulkDelete(keys);
}

// ============================================================
// 2. FETCH AVEC RETRY (gestion 429)
// ============================================================
const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 429 && attempt < retries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : baseDelay * Math.pow(2, attempt);
        console.warn(`⏳ 429 détecté, nouvelle tentative dans ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      if (attempt === retries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const API_RAPPORT = `${API_CONFIG2.apiUrl}mission/api_rapport_mission.php`;

const RapportMissionModal = ({
  show,
  onHide,
  onSuccess,
  entrepriseId,
  missionId,
  rapportId: explicitRapportId = null,
  userId = null,
  missionData = null
}) => {
  const [form, setForm] = useState({
    id_entreprise: entrepriseId || '',
    id_mission: missionId || '',
    cible: '',
    type_mission: '',
    commentaire: '',
    notes: 0,
    statut: 'Brouillon',
    dates: new Date().toISOString().slice(0, 10)
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [internalRapportId, setInternalRapportId] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [dataSource, setDataSource] = useState('cache');
  const abortRef = useRef(null);

  // Options pour les select
  const cibles = ['Agent nettoyage', 'Chauffeur', 'Gardiennage', 'Autres'];
  const types = ['Inspection', 'Audit', 'Formation', 'Supervision', 'Sensibilisation', 'Contrôle', 'Installation'];
  const statuts = ['Brouillon', 'En cours', 'Terminé', 'Validée'];

  // --- Vérification automatique de l'existence d'un rapport (avec cache) ---
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const checkExistingRapport = async () => {
      if (explicitRapportId) {
        setInternalRapportId(explicitRapportId);
        setDataSource('explicite');
        // On pourrait charger les données ici, mais on les obtient via la requête single plus tard
        // On va charger via l'appel unique ci-dessous
        await loadRapportData(explicitRapportId);
        return;
      }

      if (!missionId || !show) {
        setInternalRapportId(null);
        return;
      }

      setIsChecking(true);
      setLoading(true);
      const cacheKey = `rapport_existence_${missionId}`;

      try {
        // 1. Essayer le cache
        const cached = await getCached(cacheKey);
        if (cached && cached.exists) {
          setInternalRapportId(cached.rapportId);
          setDataSource('cache');
          // Charger les données du rapport depuis le cache si elles existent
          const dataCacheKey = `rapport_data_${cached.rapportId}`;
          const cachedData = await getCached(dataCacheKey);
          if (cachedData) {
            setForm({
              id_entreprise: cachedData.id_entreprise,
              id_mission: cachedData.id_mission,
              cible: cachedData.cible,
              type_mission: cachedData.type_mission,
              commentaire: cachedData.commentaire,
              notes: cachedData.notes,
              statut: cachedData.statut,
              dates: cachedData.dates
            });
            setDataSource('cache');
            setLoading(false);
            setIsChecking(false);
            return;
          }
          // Sinon, on va chercher les données en direct
        }

        // 2. Requête API (avec retry)
        const url = `${API_RAPPORT}?action=list&id_mission=${missionId}&id_entreprise=${entrepriseId || ''}`;
        const data = await fetchWithRetry(url, { signal: controller.signal });

        if (data.status === 'success' && data.data && data.data.length > 0) {
          const latest = data.data[0];
          setInternalRapportId(latest.id);
          setForm({
            id_entreprise: latest.id_entreprise,
            id_mission: latest.id_mission,
            cible: latest.cible,
            type_mission: latest.type_mission,
            commentaire: latest.commentaire,
            notes: latest.notes,
            statut: latest.statut,
            dates: latest.dates
          });
          setDataSource('live');
          setError(null);
          // Mettre en cache l'existence et les données
          await setCached(cacheKey, { exists: true, rapportId: latest.id });
          await setCached(`rapport_data_${latest.id}`, latest);
        } else {
          // Aucun rapport
          setInternalRapportId(null);
          setDataSource('cache');
          setForm({
            id_entreprise: entrepriseId || '',
            id_mission: missionId || '',
            cible: '',
            type_mission: '',
            commentaire: '',
            notes: 0,
            statut: 'Brouillon',
            dates: new Date().toISOString().slice(0, 10)
          });
          await setCached(cacheKey, { exists: false });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message);
        // Fallback : tenter d'utiliser le cache expiré
        const cached = await getCached(cacheKey);
        if (cached && cached.exists) {
          setInternalRapportId(cached.rapportId);
          setDataSource('cache (expiré)');
          const dataCacheKey = `rapport_data_${cached.rapportId}`;
          const cachedData = await getCached(dataCacheKey);
          if (cachedData) {
            setForm({
              id_entreprise: cachedData.id_entreprise,
              id_mission: cachedData.id_mission,
              cible: cachedData.cible,
              type_mission: cachedData.type_mission,
              commentaire: cachedData.commentaire,
              notes: cachedData.notes,
              statut: cachedData.statut,
              dates: cachedData.dates
            });
          }
        } else {
          setInternalRapportId(null);
        }
      } finally {
        setLoading(false);
        setIsChecking(false);
      }
    };

    // Fonction pour charger les données d'un rapport spécifique (si explicitRapportId)
    const loadRapportData = async (id) => {
      setLoading(true);
      try {
        const cacheKey = `rapport_data_${id}`;
        const cached = await getCached(cacheKey);
        if (cached) {
          setForm({
            id_entreprise: cached.id_entreprise,
            id_mission: cached.id_mission,
            cible: cached.cible,
            type_mission: cached.type_mission,
            commentaire: cached.commentaire,
            notes: cached.notes,
            statut: cached.statut,
            dates: cached.dates
          });
          setDataSource('cache');
          setLoading(false);
          return;
        }
        const data = await fetchWithRetry(`${API_RAPPORT}?action=single&id=${id}`, { signal: controller.signal });
        if (data.status === 'success') {
          const d = data.data;
          setForm({
            id_entreprise: d.id_entreprise,
            id_mission: d.id_mission,
            cible: d.cible,
            type_mission: d.type_mission,
            commentaire: d.commentaire,
            notes: d.notes,
            statut: d.statut,
            dates: d.dates
          });
          setDataSource('live');
          await setCached(cacheKey, d);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      checkExistingRapport();
    } else {
      setInternalRapportId(null);
      setLoading(false);
      setError(null);
    }

    return () => {
      controller.abort();
    };
  }, [show, missionId, entrepriseId, explicitRapportId]);

  // --- Vidage du cache des rapports ---
  const handleClearCache = async () => {
    try {
      await clearRapportCache();
      setToast({ msg: 'Cache des rapports vidé ✓', variant: 'success' });
      setTimeout(() => setToast(null), 3000);
      // Re-vérifier l'existence pour rafraîchir
      if (missionId) {
        setIsChecking(true);
        setLoading(true);
        // On force la requête en supprimant l'entrée locale
        await clearRapportCache();
        // Re-exécuter la vérification (le useEffect se relancera automatiquement si on change show ?)
        // On déclenche un rechargement manuel
        const cacheKey = `rapport_existence_${missionId}`;
        await db.missions.delete(cacheKey);
        const url = `${API_RAPPORT}?action=list&id_mission=${missionId}&id_entreprise=${entrepriseId || ''}`;
        const data = await fetchWithRetry(url);
        if (data.status === 'success' && data.data && data.data.length > 0) {
          const latest = data.data[0];
          setInternalRapportId(latest.id);
          setForm({
            id_entreprise: latest.id_entreprise,
            id_mission: latest.id_mission,
            cible: latest.cible,
            type_mission: latest.type_mission,
            commentaire: latest.commentaire,
            notes: latest.notes,
            statut: latest.statut,
            dates: latest.dates
          });
          setDataSource('live');
          await setCached(cacheKey, { exists: true, rapportId: latest.id });
          await setCached(`rapport_data_${latest.id}`, latest);
        } else {
          setInternalRapportId(null);
          setForm({
            id_entreprise: entrepriseId || '',
            id_mission: missionId || '',
            cible: '',
            type_mission: '',
            commentaire: '',
            notes: 0,
            statut: 'Brouillon',
            dates: new Date().toISOString().slice(0, 10)
          });
          await setCached(cacheKey, { exists: false });
        }
        setIsChecking(false);
        setLoading(false);
      }
    } catch (err) {
      setToast({ msg: 'Erreur lors du vidage du cache', variant: 'danger' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_entreprise || !form.id_mission || !form.cible || !form.type_mission || !form.commentaire || !form.dates) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSubmitting(true);
    setError(null);
    const payload = {
      id_entreprise: parseInt(form.id_entreprise),
      id_mission: parseInt(form.id_mission),
      cible: form.cible,
      type_mission: form.type_mission,
      commentaire: form.commentaire,
      notes: parseInt(form.notes) || 0,
      statut: form.statut,
      dates: form.dates
    };
    try {
      let url, options;
      const isUpdate = internalRapportId !== null;
      if (isUpdate) {
        payload.id = internalRapportId;
        url = `${API_RAPPORT}?action=update&user_id=${userId || ''}`;
        options = { method: 'PUT', body: JSON.stringify(payload) };
      } else {
        url = `${API_RAPPORT}?action=create&user_id=${userId || ''}`;
        options = { method: 'POST', body: JSON.stringify(payload) };
      }
      const data = await fetchWithRetry(url, {
        ...options,
        headers: { 'Content-Type': 'application/json' }
      });
      if (data.status !== 'success') throw new Error(data.message || 'Erreur');
      setToast({ msg: isUpdate ? 'Rapport mis à jour ✓' : 'Rapport créé ✓', variant: 'success' });
      setTimeout(() => setToast(null), 4000);
      // Invalider le cache après modification
      const cacheKey = `rapport_existence_${form.id_mission}`;
      await db.missions.delete(cacheKey);
      await db.missions.delete(`rapport_data_${isUpdate ? internalRapportId : data.id}`);
      if (onSuccess) onSuccess(data);
      onHide();
    } catch (err) {
      setError(err.message);
      setToast({ msg: err.message, variant: 'danger' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: #e0e7ff;
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 1.5rem;
        }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 0.8rem 1.5rem;
        }
        .modal-glass .modal-body {
          padding: 1.5rem;
        }
        .modal-glass .form-label {
          font-size: 0.85rem;
          color: #94a3b8;
          font-weight: 500;
        }
        .modal-glass .form-control, .modal-glass .form-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: #e0e7ff;
          border-radius: 12px;
          padding: 0.5rem 0.8rem;
        }
        .modal-glass .form-control:focus, .modal-glass .form-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .modal-glass .form-control::placeholder {
          color: #64748b;
        }
        .modal-glass .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.6;
        }
        .btn-glass {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: #e0e7ff;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
        }
        .btn-glass:hover {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }
        .btn-glass.primary {
          background: #3b82f6;
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.2);
        }
        .btn-glass.primary:hover {
          background: #1d4ed8;
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.35);
        }
        .btn-glass.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .btn-glass.outline:hover {
          background: rgba(255,255,255,0.04);
        }
        .btn-glass.danger-outline {
          background: transparent;
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #f87171;
        }
        .btn-glass.danger-outline:hover {
          background: rgba(239, 68, 68, 0.1);
        }
        .btn-glass .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }
        .badge-source {
          font-size: 0.6rem;
          padding: 0.1rem 0.6rem;
          border-radius: 30px;
          background: rgba(59, 130, 246, 0.12);
          color: #4f8cff;
          border: 1px solid rgba(59, 130, 246, 0.08);
          white-space: nowrap;
          margin-left: 0.5rem;
          font-weight: 400;
        }
        .badge-source.live {
          background: rgba(46, 204, 113, 0.12);
          color: #2ecc71;
          border-color: rgba(46, 204, 113, 0.08);
        }
        .badge-source.warning {
          background: rgba(245, 158, 11, 0.12);
          color: #f59e0b;
          border-color: rgba(245, 158, 11, 0.08);
        }
        @media (max-width: 576px) {
          .modal-glass .modal-content { border-radius: 20px; }
          .modal-glass .modal-header { padding: 1rem; }
          .modal-glass .modal-body { padding: 1rem; }
          .modal-glass .modal-footer { flex-direction: column-reverse; }
          .modal-glass .modal-footer button { width: 100%; justify-content: center; }
        }
        /* Toast custom */
        .toast-container {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          max-width: 400px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 12px 16px;
          color: #e0e7ff;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
        }
        .toast-item.success { border-left: 4px solid #3b82f6; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
        }
        .btn-close-custom:hover { color: #fff; }
      `}</style>

      <Modal show={show} onHide={onHide} centered backdrop="static" className="modal-glass" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faClipboardList} className="text-primary me-2" />
            {isChecking ? (
              <><Spinner animation="border" size="sm" className="me-2" /> Vérification...</>
            ) : internalRapportId ? (
              <>
                Modifier le rapport
                <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (expiré)' ? 'warning' : ''}`}>
                  {dataSource === 'cache' ? '📦 Cache' :
                   dataSource === 'cache (expiré)' ? '⚠️ Expiré' :
                   dataSource === 'live' ? '🔄 Live' :
                   dataSource === 'explicite' ? '📌 Édition' : ''}
                </span>
              </>
            ) : (
              'Nouveau rapport de mission'
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isChecking || loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-secondary">Chargement...</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faBuilding} className="me-1" /> Entreprise</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.id_entreprise}
                      disabled
                      style={{ opacity: 0.7 }}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faTasks} className="me-1" /> Mission</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.id_mission}
                      disabled
                      style={{ opacity: 0.7 }}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faUserTag} className="me-1" /> Cible <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="cible"
                      value={form.cible}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    >
                      <option  className='bg-dark text-white' value="">Sélectionner</option>
                      {cibles.map(c => <option  className='bg-dark text-white' key={c} value={c}>{c}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faClipboardCheck} className="me-1" /> Type de mission <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="type_mission"
                      value={form.type_mission}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    >
                      <option className='bg-dark text-white' value="">Sélectionner</option>
                      {types.map(t => <option  className='bg-dark text-white' key={t} value={t}>{t}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faStar} className="me-1" /> Note (0-10)</Form.Label>
                    <Form.Control
                      type="number"
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      min="0"
                      max="10"
                      disabled={submitting}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label><FontAwesomeIcon icon={faCalendarAlt} className="me-1" /> Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="dates"
                      value={form.dates}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label><FontAwesomeIcon icon={faComment} className="me-1" /> Commentaire <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="commentaire"
                  value={form.commentaire}
                  onChange={handleChange}
                  placeholder="Détails du rapport..."
                  required
                  disabled={submitting}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  name="statut"
                  value={form.statut}
                  onChange={handleChange}
                  disabled={submitting}
                >
                  {statuts.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>

              {error && (
                <div className="alert alert-danger alert-glass-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  {error}
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                <div className="d-flex gap-2">
                  <Button
                    variant="secondary"
                    className="btn-glass outline"
                    onClick={handleClearCache}
                    disabled={submitting}
                    title="Vider le cache des rapports"
                  >
                    <FontAwesomeIcon icon={faEraser} /> Vider le cache
                  </Button>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="secondary" className="btn-glass outline" onClick={onHide} disabled={submitting}>
                    <FontAwesomeIcon icon={faTimes} /> Annuler
                  </Button>
                  <Button variant="primary" type="submit" className="btn-glass primary" disabled={submitting}>
                    {submitting ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSave} className="me-1" />}
                    {internalRapportId ? 'Mettre à jour' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default RapportMissionModal;